import { Head } from 'vite-react-ssg';
import { jsonLd } from '@/components/SEO';

export interface FAQItem {
  q: string;
  a: string;
}

interface FAQProps {
  items: FAQItem[];
  title?: string;
  /** Emit FAQPage JSON-LD (default true). Set false when the page already emits one. */
  schema?: boolean;
}

// Native details/summary accordion — works without JS, keyboard-operable,
// and the answers are in the prerendered HTML for crawlers. Emits FAQPage
// JSON-LD from the same items. This describes the visible content; it does not
// promise rich results or inclusion in an answer engine.
const FAQ = ({ items, title = 'Questions', schema = true }: FAQProps) => {
  if (!items.length) return null;
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((i) => ({
      '@type': 'Question',
      name: i.q,
      acceptedAnswer: { '@type': 'Answer', text: i.a },
    })),
  };
  return (
    <section aria-labelledby="faq-heading">
      {schema && (
        <Head>
          <script type="application/ld+json">{jsonLd(faqSchema)}</script>
        </Head>
      )}
      <h2 id="faq-heading" className="text-subhead">
        {title}
      </h2>
      <div className="mt-6 border-t border-rule">
        {items.map((i, idx) => (
          <details key={idx} className="group border-b border-rule">
            <summary className="flex cursor-pointer items-start justify-between gap-6 py-5 text-small hover:underline hover:underline-offset-[0.3em]">
              <span>{i.q}</span>
              <span aria-hidden className="mt-0.5 shrink-0 select-none text-[1.25rem] leading-none group-open:hidden">
                +
              </span>
              <span aria-hidden className="mt-0.5 hidden shrink-0 select-none text-[1.25rem] leading-none group-open:inline">
                –
              </span>
            </summary>
            <p className="pb-6 pr-8 text-body">{i.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
};

export default FAQ;
